/** Format a YYYY-MM-DD (or ISO) date as MM/DD/YYYY without timezone shifting. */
export function fmtDate(value?: string | null): string {
  if (!value) return "-";
  const [y, m, d] = value.slice(0, 10).split("-");
  if (!y || !m || !d) return value;
  return `${m}/${d}/${y}`;
}

/** Today's local date as YYYY-MM-DD. */
export function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Authorization countdown. Far-future dates still say how long is left. */
export function authCountdown(days: number): string {
  if (days < 0) return `expired ${-days}d ago`;
  if (days === 0) return "ends today";
  return `${days}d left`;
}

/** Review or due-date countdown. Far-future dates still say how long until due. */
export function dueCountdown(days: number): string {
  if (days < 0) return `overdue ${-days}d`;
  if (days === 0) return "due today";
  return `in ${days}d`;
}

/** "1 draft" or "2 drafts". */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Age of a past contact. `days` is daysUntil(contact date): 0 today,
 * negative when the visit already happened.
 */
export function contactAge(days: number): string {
  if (days === 0) return "today";
  if (days > 0) return `in ${days}d`;
  return `${-days}d ago`;
}

/** Whole days from today until a YYYY-MM-DD date (negative when past). */
export function daysUntil(value?: string | null): number | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const target = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/** Authorizations within this many days of ending are flagged. */
/** Default only - clinics set their own in Settings → PRP Program (usePrpSettings). */
export const AUTH_WARNING_DAYS = 30;

/** Assumed IRP review cycle - confirm with the program (plan §3.8). */
/** Default only - clinics set their own in Settings → PRP Program (usePrpSettings). */
export const IRP_REVIEW_DAYS = 180;

/** Add days to a YYYY-MM-DD date, returning YYYY-MM-DD. */
export function addDays(value: string, days: number): string {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + days * 86_400_000);
  return t.toISOString().slice(0, 10);
}
